import { Edge, Node } from 'reactflow';
import { NodesTypes, parentNodeTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  ConceptualSubLayerTypes,
  ContextualSubLayerTypes,
  SubLayerTypes,
} from '@constants/canvas/layers';
import {
  matchesModelFiltersDto,
  ModelFilters,
} from '@context/ProjectFilters/matchesModelFilters';
import { questionnaireViewFilters } from '@context/ProjectFilters/questionnaireViewFilters';
import {
  ICanvasNodesResponse,
  NodeDTOTypes,
} from '@store/services/nodes/types';

type OtherModeNode = ICanvasNodesResponse['nodes'][number];

export const hideNodesOutsideFocus = (
  nodes: Node[],
  isFocusActive: boolean,
  focusedIds: string[],
): Node[] => {
  if (!isFocusActive) return nodes;

  const focusedSet = new Set(focusedIds);

  return nodes.map((node) =>
    node.type === NodesTypes.CustomNode && !focusedSet.has(node.id)
      ? { ...node, hidden: true }
      : node,
  );
};

export const unselectNodes = (nodes: Node[]): Node[] =>
  nodes.map((node) => (node.selected ? { ...node, selected: false } : node));

export const unselectEdges = (edges: Edge[]): Edge[] =>
  edges.map((edge) =>
    edge.selected ? { ...edge, selected: false, animated: false } : edge,
  );

export const getVisibleNodeIds = (nodes: Node[]): Set<string> =>
  new Set(nodes.filter(({ hidden }) => !hidden).map(({ id }) => id));

export const getExportEntityIds = (nodes: Node[]): string[] =>
  nodes.reduce<string[]>((acc, { id, hidden, type }) => {
    if (!hidden && type === NodesTypes.CustomNode) acc.push(id);

    return acc;
  }, []);

const getSubLayer = (node: OtherModeNode): SubLayerTypes | undefined =>
  parentNodeTypes[node.type as keyof typeof parentNodeTypes];

const AncestorSubLayers: SubLayerTypes[] = [
  ContextualSubLayerTypes.StrategicEnablers,
  ContextualSubLayerTypes.StrategicPriorities,
  ConceptualSubLayerTypes.BusinessAttributes,
  ConceptualSubLayerTypes.SecurityAttributes,
];

const isCiaControl = (subLayer: SubLayerTypes | undefined) =>
  !!subLayer && (CiaSubLayers as SubLayerTypes[]).includes(subLayer);

/**
 * The entities View Controls keeps in the Contextual and Conceptual layers:
 * every complete ancestor of a kept Service, transitively, reached through
 * complete, kept nodes — the canvas's rule for the view, on plain DTOs. The
 * walk only ever narrows what `isKept` allowed, so connectivity cannot bring
 * back an entity another category hid.
 */
const collectAncestorsOfKeptServices = (
  nodes: OtherModeNode[],
  isKept: (node: OtherModeNode) => boolean,
) => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const ancestorIds = new Set<string>();

  const traverseParents = (nodeId: string) => {
    const node = nodesById.get(nodeId);

    if (!node || ancestorIds.has(nodeId)) return;
    if (!node.is_complete || !isKept(node)) return;

    ancestorIds.add(nodeId);
    node.parentsIds?.forEach(traverseParents);
  };

  nodes.forEach((node) => {
    if (node.type !== NodeDTOTypes.Service || !isKept(node)) return;

    node.parentsIds?.forEach(traverseParents);
  });

  return ancestorIds;
};

/**
 * The other mode's entities an export includes: those outside the current
 * mode's sub-layers that the one shared predicate keeps, Scope included.
 *
 * While a questionnaire result (View Controls) is active the canvas's rules
 * for the view apply here too: each entity is judged against the selection
 * `questionnaireViewFilters` gives it, a CIA control that is unreported or
 * incomplete is dropped, and a Contextual or Conceptual entity stays only as
 * a complete ancestor of a kept Service. Without a result no connectivity
 * rule runs, so no filter ever resurrects the ancestors of the entities it
 * keeps.
 *
 * The nodes are plain DTOs — the current mode's request never fetched them —
 * which is why the predicate is called in its DTO form.
 */
export const getOtherModeEntityIds = ({
  nodes,
  currentModeSubLayers,
  modelFilters,
  hasQuestionnaireResult = false,
}: {
  nodes: OtherModeNode[];
  currentModeSubLayers: Set<string>;
  modelFilters: ModelFilters;
  hasQuestionnaireResult?: boolean;
}): string[] => {
  const isKept = (node: OtherModeNode) => {
    const subLayer = getSubLayer(node);

    const isMatch = matchesModelFiltersDto(
      node,
      subLayer,
      questionnaireViewFilters(
        modelFilters,
        node,
        subLayer,
        hasQuestionnaireResult,
      ),
    );

    if (!isMatch || !hasQuestionnaireResult || !isCiaControl(subLayer)) {
      return isMatch;
    }

    return node.is_reported !== false && Boolean(node.is_complete);
  };

  const connectedAncestorIds = hasQuestionnaireResult
    ? collectAncestorsOfKeptServices(nodes, isKept)
    : null;

  return nodes.reduce<string[]>((acc, node) => {
    const subLayer = getSubLayer(node);

    if (!subLayer || currentModeSubLayers.has(subLayer)) return acc;

    const isIncluded =
      connectedAncestorIds && AncestorSubLayers.includes(subLayer)
        ? connectedAncestorIds.has(node.id)
        : isKept(node);

    if (isIncluded) acc.push(node.id);

    return acc;
  }, []);
};
